/**
 * OnlyOffice — lógica pura do editor (Story E14.2.3).
 *
 * Camada SEM React/DOM: tipos do contrato com o backend (E14.2.2), construção
 * dos props do `@onlyoffice/document-editor-react` a partir da sessão, predicado
 * de "este doc abre no OnlyOffice?" e helpers de id/instância. Isolar isto aqui
 * deixa o coração da story testável em vitest sem montar o editor real.
 *
 * Os imports do pacote são `import type` (apagados em runtime) — os testes desta
 * lib não carregam o pacote pesado; só o componente `OnlyOfficeIframe` o importa
 * de verdade.
 */
import type { DocumentEditorProps } from "@onlyoffice/document-editor-react";
import type { Config, DocEditor } from "@onlyoffice/doceditor-types";

/** Resposta de `POST /onlyoffice/sessions/{id}` (espelha `EditorSessionResponse` do backend). */
export interface EditorSessionResponse {
  /** URL pública do Document Server (browser → DS); vai em `documentServerUrl`. */
  document_server_url: string;
  /** Config do editor já assinado pelo backend em `config.token` — passado AS-IS. */
  config: Config;
  /** Atalho pra `config.token` (não usado diretamente; o config já o carrega). */
  token: string;
}

/** Callbacks de alto nível que o dialog liga aos eventos do editor. */
export interface OnlyOfficeEventHandlers {
  /** Documento carregado e pronto. */
  onReady?: () => void;
  /** Estado de alterações: `true` = há alterações ainda não persistidas. */
  onStateChange?: (dirty: boolean) => void;
  /** Erro do editor OU falha de carregamento do componente (`onLoadComponentError`). */
  onError?: (info: unknown) => void;
}

/** Extensões Office que o OnlyOffice edita/visualiza (view+edit, gating é do backend). */
const OFFICE_EDITABLE_EXTS = new Set([
  "docx",
  "xlsx",
  "pptx",
  "doc",
  "xls",
  "ppt",
  "odt",
  "ods",
  "odp",
]);

/** Extensão (minúscula, sem ponto) derivada do nome do arquivo, ou "". */
function extensionOf(name: string | null | undefined): string {
  if (!name) return "";
  const dot = name.lastIndexOf(".");
  if (dot < 0 || dot === name.length - 1) return "";
  return name.slice(dot + 1).toLowerCase();
}

/**
 * O documento abre no OnlyOffice? Decide por `file_extension` (a `DocumentItem`
 * da lista NÃO traz `mime_type`), com fallback pra extensão derivada do nome.
 */
export function isOnlyOfficeEditable(doc: {
  file_extension?: string | null;
  name?: string | null;
}): boolean {
  const raw = (doc.file_extension || extensionOf(doc.name) || "").toLowerCase();
  const ext = raw.replace(/^\./, "");
  return OFFICE_EDITABLE_EXTS.has(ext);
}

/** Id estável do editor (host div + chave em `window.DocEditor.instances`). */
export function editorIdFor(documentId: string): string {
  return `onlyoffice-${documentId}`;
}

/** Instância viva do editor (debug/cleanup); o pacote já destrói no unmount. */
export function getEditorInstance(editorId: string): DocEditor | undefined {
  if (typeof window === "undefined") return undefined;
  return window.DocEditor?.instances?.[editorId];
}

/**
 * Monta os props do `DocumentEditor` a partir da sessão do backend. O `config`
 * vai AS-IS (já assinado em `config.token` — nunca re-assinamos no cliente). Os
 * eventos do editor são normalizados pros callbacks de alto nível.
 */
export function buildEditorProps(
  session: EditorSessionResponse,
  editorId: string,
  handlers: OnlyOfficeEventHandlers
): DocumentEditorProps {
  return {
    id: editorId,
    documentServerUrl: session.document_server_url,
    config: session.config,
    height: "100%",
    width: "100%",
    events_onDocumentReady: () => handlers.onReady?.(),
    events_onDocumentStateChange: (event: object) => {
      // OnlyOffice envia `{ data: boolean }` — true quando há alterações não-salvas.
      const dirty = Boolean((event as { data?: unknown })?.data);
      handlers.onStateChange?.(dirty);
    },
    events_onError: (event: object) => handlers.onError?.(event),
    onLoadComponentError: (errorCode: number, errorDescription: string) =>
      handlers.onError?.({ errorCode, errorDescription }),
  };
}

/** Status HTTP de um erro do axios (ou undefined se não-HTTP). */
export function editorErrorStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status;
}

/** Mensagem PT-BR pro usuário conforme o status da falha ao abrir a sessão. */
export function editorErrorMessage(error: unknown): string {
  switch (editorErrorStatus(error)) {
    case 503:
      return "Edição OnlyOffice não disponível neste ambiente.";
    case 403:
      return "Você não tem permissão para abrir este documento.";
    case 404:
      return "Documento não encontrado.";
    default:
      return "Não foi possível abrir o editor. Tente novamente.";
  }
}

/** Falha definitiva (não adianta re-tentar): feature ausente (503) ou sem permissão (403/404). */
export function isTerminalEditorError(error: unknown): boolean {
  const status = editorErrorStatus(error);
  return status === 503 || status === 403 || status === 404;
}
